// Step-aside on the processor fee (issue #110): POST /api/finance/processor-fees/step-aside.
// The "I'll handle it myself" secondary action on the armed fee pin. Like the period-scoped
// Record path it carries ONLY the processor identity plus the treasurer's disposition (reason +
// optional note); the server resolves the OWED period itself (never sent from the frontend),
// records a first-class period+processor step-aside + its forensic audit row, and the federal
// must-record gate then proceeds for that period+processor. After a step-aside the armed pin
// disappears (the display lines drop it): the step-aside in the write set refreshes the preflight
// that shows the pin.

import { useMutation } from '@tanstack/react-query';
import type { StepAsideProcessorFeeRequest } from '@signalvote/shared';
import { apiVoid } from './apiFetch';

/** Typed error carrying the server's message for a failed step-aside. */
export class StepAsideProcessorFeeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StepAsideProcessorFeeError';
  }
}

/**
 * Step aside on the owed-period processor fee. Sends the processor identity + the treasurer's
 * disposition (reason + optional note); the server resolves the owed period. The write set
 * refreshes the preflight so the armed pin disappears.
 */
export function useStepAsideProcessorFee() {
  return useMutation<void, Error, StepAsideProcessorFeeRequest>({
    mutationFn: ({ processorId, reason, note }) =>
      apiVoid('/api/finance/processor-fees/step-aside', {
        method: 'POST',
        json: { processorId, reason, note },
        error: (error) =>
          new StepAsideProcessorFeeError(error.error ?? 'Failed to step aside on the processor fee'),
      }),
  });
}
